import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  WorkflowDetailsImageUpload,
  createWorkflow,
  getWorkflowByID,
  updateWorkflow,
} from "./services/workflow.services";

import { clearWorkflowDetails } from "./workflowSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";

const CreateWorkflow: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const WorkflowID = location.state?.WorkflowID;
  const WorkflowDetail = useAppSelector(
    (state) => state.workflow.WorkflowDetail
  );
  const loading = useAppSelector((state) => state.workflow.loading);
  const [formKey, setFormKey] = React.useState(1);

  useEffect(() => {
    if (WorkflowID) {
      dispatch(getWorkflowByID(WorkflowID));
    }
    return () => {
      dispatch(clearWorkflowDetails());
    };
  }, [WorkflowID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [WorkflowDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ? values.Title : "",
      SequenceNo: values.SequenceNo ? values.SequenceNo : null,
      ShortDescription: values.ShortDescription ? values.ShortDescription : "",
      LongDescription: values.LongDescription ? values.LongDescription : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? values.MainImage[0].getRawFile()
        : "";
    const OtherImage =
      values?.OtherImage && typeof values?.OtherImage !== "string"
        ? values.OtherImage[0].getRawFile()
        : "";

    try {
      if (WorkflowID) {
        const editPayload = {
          ...formData,
          ID: WorkflowID,
        };

        const response = await dispatch(updateWorkflow(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.OtherImage && typeof values?.OtherImage !== "string")
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("OtherImage", OtherImage);
            ImageData.append("ID", WorkflowID);
            await dispatch(WorkflowDetailsImageUpload(ImageData));
          }
          navigate("/workflow");
        }
      } else {
        const response = await dispatch(createWorkflow(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.MainImage && typeof values?.MainImage !== "string") ||
              (values?.OtherImage && typeof values?.OtherImage !== "string")) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("MainImage", MainImage);
            ImageData.append("OtherImage", OtherImage);
            ImageData.append("ID", response?.payload?.Data?.ID);
            await dispatch(WorkflowDetailsImageUpload(ImageData));
          }
          navigate("/workflow");
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          key={formKey}
          onSubmit={handleSubmit}
          initialValues={WorkflowDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {WorkflowID ? "Update Workflow" : "Add Workflow"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    rowSpan={3}
                    className="d-flex gap-2"
                    colSpan={3}
                  >
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                    {/* </GridLayoutItem>
                  <GridLayoutItem rowSpan={3} colSpan={2}> */}
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="LongDescription"
                      label="Long Description"
                      placeholder="Long Description"
                      component={FormRichTextField}
                      style={{ height: 150 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      accept="image/*"
                      alternateName="Main Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Field
                      justifyContent="start"
                      name={`OtherImage`}
                      label="Other Image"
                      type="file"
                      accept="image/*"
                      alternateName="Other Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem style={{ width: "100%", paddingTop: "10px" }}>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 10,
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={WorkflowID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/workflow")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateWorkflow;
